import useComponentPosition from 'hooks/useComponentPosition';
import {
    LayoutConditional,
    LayoutMethod,
} from 'hooks/useComponentPosition/types';
import {HTMLAttributes, ReactNode, useRef} from 'react';
import {createPortal} from 'react-dom';
import {twMerge} from 'tailwind-merge';

import useOverlayContainer from './utils';

type LayoutLayer = 'z-dropdown' | 'z-modal' | 'z-tooltip';

type OverlayProps<L extends LayoutMethod> = {
    boundaryElement?: HTMLElement | null;
    children: ((layout: LayoutConditional<L>) => ReactNode) | ReactNode;
    className?: string;
    element?: HTMLDivElement | HTMLElement | null;
    layoutLayer: LayoutLayer;
    layoutMethod: L;
    layoutOffset?: number;
    matchWidth?: boolean;
} & Omit<HTMLAttributes<HTMLDivElement>, 'children'>;

const Overlay = <L extends LayoutMethod>({
    boundaryElement,
    children,
    className,
    element,
    layoutLayer,
    layoutMethod,
    layoutOffset,
    matchWidth,
    ...props
}: OverlayProps<L>) => {
    const containerOuter = useOverlayContainer();

    const containerInner = useRef<HTMLDivElement | null>(null);

    const {layout, style} = useComponentPosition({
        boundaryElement,
        containerInner: containerInner.current,
        containerOuter,
        element,
        layoutMethod,
        layoutOffset,
        matchWidth,
    });

    return createPortal(
        <div
            className={twMerge('adjacent-overlay', layoutLayer, className)}
            data-layout={layout}
            data-modal-safe={layoutMethod === 'modal' ? undefined : 'true'}
            ref={containerInner}
            style={style}
            {...props}
        >
            {typeof children === 'function' ? children(layout) : children}
        </div>,
        containerOuter,
    );
};
export default Overlay;
